






























































































































































































































































































































import { Vue, Prop, Watch } from 'vue-property-decorator'
import Component from 'vue-class-component'
import OrderLine from '@/models/order/OrderLine'
import { vxm } from '@/store'

type Product = {
  id: string
  eontyreDescription: string
  manufacturerProductId?: string
}

type Group = {
  orderLines: OrderLine[]
  createMethod: 'existing' | 'new'
  productId: string
  productName: string
  manufacturerProductId: string
  eontyreProductId: number
  quantity: number
  unitPrice: number
  possibleProducts: Product[]
  searchManufacturerProductId: string
  searchEontyreProductId: string
  searching: boolean
  hasSearched: boolean
  selectedProductId: string
  selectedProduct: Product | null
  newManufacturerProductId: string
}

@Component({
  components: {},
})
export default class MountCompleteWheelsDialog extends Vue {
  @Prop({ type: Boolean, required: false, default: false })
  private value: boolean

  @Prop({ type: Number, required: true })
  private orderId: number

  private isLoading = true

  private locations = []
  private locationId: number | null = null

  private stockPositionId: number | null = null
  private stockPositions = []
  private stockPositionsLoading = false

  private orderLines: OrderLine[] = []
  private groups: Group[] = []
  private draggedItem: OrderLine | null = null

  private isSubmitting = false
  private showValidationErrors = false

  private locationRules = [(v: string) => !!v || this.$t('This field is mandatory')]

  private positionRules = [(v: string) => !!v || this.$t('This field is mandatory')]

  private quantityRules = [
    (v: number) => !!v || this.$t('This field is mandatory'),
    (v: number) => (v && v > 0) || this.$t('This field must be greater than 0'),
  ]

  private unitPriceRules = [
    (v: number) => !!v || this.$t('This field is mandatory'),
    (v: number) => (v && v > 0) || this.$t('This field must be greater than 0'),
  ]

  private productIdRules = [(v: string) => !!v || this.$t('This field is mandatory')]

  private async created() {
    try {
      const [locationsResponse, orderLinesResponse] = await Promise.all([
        this.$axios.get('/v4/site/locations'),
        this.$axios.get('/v4/site/order/' + this.orderId + '/lines'),
      ])

      this.orderLines = orderLinesResponse.data.data.map((line: Record<string, unknown>) => new OrderLine(line))
      this.locations = locationsResponse.data.data
    } catch (error) {
      await vxm.alert.onAxiosError(error, 'Error loading initial data')
    } finally {
      this.isLoading = false
    }
  }

  private get visible() {
    return this.value
  }

  private set visible(value) {
    this.$emit('input', value)
  }

  private get ungroupedOrderLines(): OrderLine[] {
    const groupedIds = this.groups.flatMap((group) => group.orderLines.map((line) => line.id))
    return this.orderLines.filter((line) => !groupedIds.includes(line.id) && [1, 2, 45].includes(line.productType))
  }

  private createNewGroup() {
    const newGroup: Group = {
      orderLines: [],
      createMethod: 'new',
      productId: '',
      productName: '',
      manufacturerProductId: null,
      eontyreProductId: null,
      quantity: null,
      unitPrice: null,
      possibleProducts: [],
      searchManufacturerProductId: '',
      searchEontyreProductId: '',
      searching: false,
      hasSearched: false,
      selectedProductId: '',
      selectedProduct: null,
      newManufacturerProductId: '',
    }
    this.groups.push(newGroup)
  }

  private removeGroup(groupIndex: number) {
    this.groups.splice(groupIndex, 1)
  }

  private startDrag(line: OrderLine) {
    this.draggedItem = line
  }

  private isValidDrop(group: Group): boolean {
    if (!this.draggedItem) return false

    const productType = this.draggedItem.productType
    const groupTypes = group.orderLines.map((line) => line.productType)

    // Check if adding this item would violate group rules
    if (productType === 1) {
      // Can only have one productType = 1
      return !groupTypes.includes(1)
    } else if (productType === 2) {
      // Can only have one productType = 2
      return !groupTypes.includes(2)
    } else if (productType === 45) {
      // Can only have one productType = 45
      return !groupTypes.includes(45)
    }

    return false // Only allow types 1, 2, 45
  }

  private dropItem(group: Group, event: DragEvent) {
    event.preventDefault()
    if (this.draggedItem && this.isValidDrop(group)) {
      group.orderLines.push(this.draggedItem)
      group.quantity = Math.min(...group.orderLines.map((orderLine) => orderLine.number))
      console.log(group.orderLines)
      group.unitPrice = group.orderLines.reduce((sum, item) => sum + item.priceExchanged, 0)
      this.draggedItem = null
    }
  }

  private removeFromGroup(group: Group, line: OrderLine) {
    const index = group.orderLines.findIndex((l) => l.id === line.id)
    if (index > -1) {
      group.orderLines.splice(index, 1)
    }
  }

  private validateGroup(group: Group): string | null {
    const types = group.orderLines.map((line) => line.productType)
    const hasType1 = types.includes(1)
    const hasType2 = types.includes(2)
    const type45Count = types.filter((t) => t === 45).length

    if (!hasType1 && !hasType2) {
      return this.$t('c:order-mount-wheels:Group needs one tire and one rim')
    } else if (!hasType1) {
      return this.$t('c:order-mount-wheels:Group needs one tire')
    } else if (!hasType2) {
      return this.$t('c:order-mount-wheels:Group needs one rim')
    } else if (type45Count > 1) {
      return this.$t('c:order-mount-wheels:Group can have at most one TPMS')
    }

    // Check quantity validation
    if (group.quantity > 0) {
      if (group.orderLines.length > 0) {
        const minQuantity = Math.min(...group.orderLines.map((line) => line.number))
        if (group.quantity > minQuantity) {
          return this.$t('c:order-mount-wheels:Quantity cannot be higher than the minimum quantity of its items')
        }
      }
    } else {
      return this.$t('c:order-mount-wheels:Quantity must be set and higher then 0')
    }

    if (group.createMethod === 'existing' && !group.selectedProductId) {
      return this.$t('c:order-mount-wheels:Please select an existing product')
    }

    return null
  }

  private getProductTypeClass(productType: number): string {
    switch (productType) {
      case 1:
        return 'product-type-1'
      case 2:
        return 'product-type-2'
      case 45:
        return 'product-type-45'
      default:
        return 'product-type-other'
    }
  }

  private getProductTypeColor(productType: number): string {
    switch (productType) {
      case 1:
        return 'blue'
      case 2:
        return 'green'
      case 45:
        return 'orange'
      default:
        return 'grey'
    }
  }

  private getProductTypeName(productType: number): string {
    switch (productType) {
      case 1:
        return this.$t('c:product-type:Tyre')
      case 2:
        return this.$t('c:product-type:Rim')
      case 45:
        return this.$t('c:product-type:TPMS Valve')
    }
  }

  private get canSubmit(): boolean {
    return !this.isSubmitting
  }

  private get hasValidationErrors(): boolean {
    if (!this.locationId || !this.stockPositionId) {
      return true
    }

    if (this.groups.length === 0) {
      return true
    }

    return this.groups.some((group) => {
      return !!this.validateGroup(group)
    })
  }

  private searchProducts(group: Group) {
    if (!group.searchManufacturerProductId && !group.searchEontyreProductId) {
      return
    }

    group.searching = true
    group.hasSearched = false
    group.possibleProducts = []
    group.selectedProductId = ''
    group.selectedProduct = null

    const params: Record<string, string | number | boolean> = {
      productType: 28, // complete wheel type
      orSearchMode: true,
    }

    if (group.searchEontyreProductId) {
      params.productId = group.searchEontyreProductId
    }
    if (group.searchManufacturerProductId) {
      params.manufacturerProductId = group.searchManufacturerProductId
    }

    const queryString = new URLSearchParams(params as Record<string, string>).toString()

    this.$axios
      .get('/v4/site/products/search?' + queryString)
      .then((response) => {
        const products = response.data.data.products as Product[]
        group.possibleProducts = products
        group.hasSearched = true

        if (products.length > 0) {
          group.createMethod = 'existing'
        } else {
          group.createMethod = 'new'
        }
      })
      .catch((err) => {
        group.possibleProducts = []
        group.hasSearched = true
        group.createMethod = 'new'
        vxm.alert.onAxiosError(err, 'Error searching products')
      })
      .finally(() => {
        group.searching = false
      })
  }

  private onProductSelected(group: Group) {
    if (group.selectedProductId) {
      group.selectedProduct = group.possibleProducts.find((p) => p.id === group.selectedProductId) || null
      group.productId = group.selectedProductId
      group.productName = group.selectedProduct?.eontyreDescription || ''
    } else {
      group.selectedProduct = null
      group.productId = ''
      group.productName = ''
    }
  }

  private submit() {
    this.showValidationErrors = true
    const form = this.$refs.form as Vue & { validate: () => boolean }
    const isValid = form.validate()

    if (!isValid || this.hasValidationErrors) {
      return
    }

    this.isSubmitting = true

    const data = {
      orderId: this.orderId,
      groups: this.groups.map((group) => ({
        orderLineIds: group.orderLines.map((line) => line.id),
        createMethod: group.createMethod,
        productId: group.createMethod === 'existing' ? group.selectedProductId : null,
        manufacturerProductId:
          group.createMethod === 'new'
            ? group.newManufacturerProductId
            : group.selectedProduct?.manufacturerProductId,
        quantity: group.quantity,
        unitPrice: group.unitPrice,
      })),
      stockPositionId: this.stockPositionId,
    }

    this.$axios
      .post('/v4/site/order/complete-wheel-mount', data)
      .then(() => {
        this.$emit('update')
        this.value = false
      })
      .catch((err) => {
        vxm.alert.onAxiosError(err, 'Error on mounting complete wheel')
      })
      .finally(() => {
        this.isSubmitting = false
      })
  }

  @Watch('locationId')
  private getPositions() {
    this.$axios
      .get('/v4/site/locations/' + this.locationId + '/positions')
      .then((response) => {
        this.stockPositions = response.data.data
      })
      .catch((err) => {
        vxm.alert.onAxiosError(err, 'Error reading location positions')
      })
      .finally(() => {
        this.stockPositionsLoading = false
      })
  }
}
